import {
	getOrderingEarnedPoints,
	getOrderingMaxPoints,
	resolveCorrectOrder,
} from "@memorang/helpers/ordering";
import type { BaseItemSchema, Choice } from "@memorang/types";
import Box from "@memorang/ui/components/Box";
import ReorderParagraph from "@memorang/ui/components/StudySession/SessionContent/item-types/ReorderParagraph";
import { useCallback, useMemo } from "react";
import useAIFeedbackStore from "../../../hooks/useAIFeedbackStore";
import useItemAnsweredState from "../../../hooks/useItemAnsweredState";
import DesktopFeedbackPanel from "./DesktopFeedbackPanel";

type Props = {
	item: BaseItemSchema;
	isSplitLayout: boolean;
};

const ReorderContainer = ({ item, isSplitLayout }: Props) => {
	const { content, response, scoring } = item;
	const itemId = item.id!;

	const { isAnswered, inReviewMode } = useItemAnsweredState(item);

	const setUserResponse = useAIFeedbackStore((s) => s.setUserResponse);
	const storedResponse = useAIFeedbackStore((s) => s.userResponse?.[itemId]);
	// Runtime answers / store entries are not always string[] (review/report
	// payloads). Guard before passing into ReorderParagraph (.map crash APPS-GTZ).
	const rawValues = Array.isArray(storedResponse)
		? storedResponse
		: item.answer?.selectedOrder;
	const values = Array.isArray(rawValues)
		? rawValues.map((id) => String(id))
		: undefined;

	const handleChangeValues = useCallback(
		(nextValues: string[]) => {
			setUserResponse(itemId, nextValues);
		},
		[itemId, setUserResponse],
	);

	const paragraphs = useMemo((): Choice[] => {
		if (content.stimulus?.type !== "TEXT" || !content.stimulus.segments) {
			return [];
		}
		return content.stimulus.segments.map((seg) => ({
			id: String(seg.id),
			text: seg.text,
		}));
	}, [content.stimulus]);

	const correctOrder = useMemo((): string[] | undefined => {
		if (scoring?.type !== "ANSWER_KEY_ORDERING") {
			return undefined;
		}
		return resolveCorrectOrder(scoring.answerKey, scoring.correctOrder);
	}, [scoring]);

	const { earnedPoints, maxPoints } = useMemo(() => {
		if (!isAnswered || scoring?.type !== "ANSWER_KEY_ORDERING") {
			return { earnedPoints: undefined, maxPoints: undefined };
		}
		return {
			earnedPoints: getOrderingEarnedPoints(
				scoring.answerKey,
				values,
				scoring.correctOrder,
			),
			maxPoints: getOrderingMaxPoints(scoring.answerKey),
		};
	}, [isAnswered, scoring, values]);

	const instruction =
		response.type === "ORDER_RECONSTRUCTION"
			? (response.instruction ?? "")
			: "";

	return (
		<Box gap={16}>
			<ReorderParagraph
				instruction={instruction}
				paragraphs={paragraphs}
				values={values}
				onChangeValues={handleChangeValues}
				disabled={isAnswered}
				isAnswered={isAnswered}
				inReviewMode={inReviewMode}
				correctOrder={isAnswered ? correctOrder : undefined}
				earnedPoints={earnedPoints}
				maxPoints={maxPoints}
			/>
			<DesktopFeedbackPanel isSplitLayout={isSplitLayout} />
		</Box>
	);
};

export default ReorderContainer;
